import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Paper,
  Group,
  Text,
  Badge,
  Button,
  Image,
  Loader,
  Center,
  TextInput,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconPlus,
  IconSettings,
  IconTrash,
  IconPencil,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import usePermissions from "../../hooks/usePermissions";
import DeviceRegistrationModal from "./DeviceRegistrationModal";
import TerminalConfigurationModal from "./TerminalConfigurationModal";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import terminalImage from "../../images/stripe-wisepos-e-terminal.jpg";

const MAX_TERMINALS = 3;
const MAX_LABEL_LENGTH = 100;

const deviceTypeToLabel = {
  simulated_wisepos_e: "WisePOS E (Simulated)",
  wisepos_e: "WisePOS E",
};

const getStatusColor = (status) => (status === "online" ? "green" : "red");

function Devices() {
  const [error, setError] = useState(null);
  const {
    businessInfo,
    currentUser,
    paymentTerminals,
    refreshPaymentTerminals,
  } = useSalon();
  const {
    registerStripeConnectTerminalDevice,
    getStripeConnectTerminalDevice,
    removeStripeTerminalDevice,
    updatePaymentTerminalLabel,
    setTerminalConfiguration,
  } = useApp();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [newDeviceData, setNewDeviceData] = useState({
    label: "",
    registrationCode: "",
  });

  // Per-reader Stripe details (label/status/serial/device_type), keyed by device id.
  const [deviceDetails, setDeviceDetails] = useState({});

  // Inline label (name) edit state.
  const [editingId, setEditingId] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");
  const [isSavingLabel, setIsSavingLabel] = useState(false);

  const perms = usePermissions();
  const isPrimaryOwner = currentUser?.id === businessInfo?.primary_owner_id;
  // Manage-only gate: viewing the terminal list rides payments.canView (the
  // Payments section gate); add/remove/rename/Configure are gated behind the
  // dedicated payments.canManageTerminals permission (Owner/Manager by default,
  // admin-configurable). The isPrimaryOwner OR-fallback is transitional so the
  // account owner is never locked out before /access serves the new key on all
  // envs; it can be dropped once the backend permission is live everywhere.
  const canManageTerminals =
    perms.can("payments", "canManageTerminals") || isPrimaryOwner;
  const accountId = businessInfo?.stripe_connect_account_id;
  const terminals = paymentTerminals || [];
  const atCap = terminals.length >= MAX_TERMINALS;

  // Fetch Stripe details for any terminal we haven't loaded yet. (deps limited
  // to the terminal list + account; the exhaustive-deps rule is not loaded by
  // this CRA base ESLint config, so a disable directive for it fails the build.)
  useEffect(() => {
    if (!accountId || !paymentTerminals) return;
    paymentTerminals.forEach((terminal) => {
      const deviceId = terminal.stripe_terminal_device_id;
      if (deviceDetails[deviceId] !== undefined) return;
      getStripeConnectTerminalDevice(deviceId, accountId)
        .then((details) =>
          setDeviceDetails((prev) => ({ ...prev, [deviceId]: details })),
        )
        .catch(() =>
          setDeviceDetails((prev) => ({ ...prev, [deviceId]: null })),
        );
    });
  }, [paymentTerminals, accountId]);

  const handleSubmit = async () => {
    setIsRegistering(true);
    setError(null);
    try {
      // Trim the name before it reaches Stripe + our cache, mirroring the inline
      // rename path — a whitespace-only label would otherwise render blank.
      const response = await registerStripeConnectTerminalDevice(
        { ...newDeviceData, label: newDeviceData.label.trim() },
        businessInfo,
      );
      if (response?.id) {
        await refreshPaymentTerminals();
        setNewDeviceData({ label: "", registrationCode: "" });
        setIsModalOpen(false);
      }
    } catch (err) {
      console.error("Error registering device:", err);
      setError(
        err?.message ||
          "Unexpected error occurred while registering device. Please try again. If the problem persists, contact support at contact@getpagoda.com",
      );
    } finally {
      setIsRegistering(false);
    }
  };

  const handleRemoveDevice = async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    setError(null);
    try {
      await removeStripeTerminalDevice(
        removeTarget.stripe_terminal_device_id,
        accountId,
        businessInfo.id,
      );
      await refreshPaymentTerminals();
      setRemoveTarget(null);
    } catch (err) {
      console.error("Error removing device:", err);
      setError(
        err?.message ||
          "Unexpected error occurred while removing device. Please try again. If the problem persists, contact support at contact@getpagoda.com",
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const startEditLabel = useCallback((terminal, fallback) => {
    setEditingId(terminal.id);
    setLabelDraft(terminal.label || fallback || "");
  }, []);

  const cancelEditLabel = useCallback(() => {
    setEditingId(null);
    setLabelDraft("");
  }, []);

  const saveLabel = async (terminal) => {
    const trimmed = labelDraft.trim();
    if (!trimmed) return; // the Stripe label / name can't be empty
    setIsSavingLabel(true);
    setError(null);
    try {
      // Updates the Stripe reader label + our cached copy.
      await updatePaymentTerminalLabel(
        terminal.id,
        businessInfo.id,
        accountId,
        trimmed,
      );
      await refreshPaymentTerminals();
      cancelEditLabel();
    } catch (err) {
      console.error("Error renaming terminal:", err);
      setError(
        err?.message || "Could not rename the terminal. Please try again.",
      );
    } finally {
      setIsSavingLabel(false);
    }
  };

  // Loading until the terminal list has resolved.
  if (paymentTerminals === null) {
    return (
      <Center mt="lg">
        <Loader color="teal" size="lg" />
      </Center>
    );
  }

  const renderTerminalCard = (terminal) => {
    const deviceId = terminal.stripe_terminal_device_id;
    const details = deviceDetails[deviceId];
    const stripeLabel = details?.label;
    const displayName =
      terminal.label || stripeLabel || `Terminal ${deviceId.slice(-4)}`;
    const isEditing = editingId === terminal.id;

    return (
      <Paper key={terminal.id} p="md" withBorder>
        <Group align="flex-start" justify="space-between">
          <Group align="flex-start">
            <Image src={terminalImage} w={120} alt="Stripe Terminal" />
            <Stack gap="xs">
              {isEditing ? (
                <TextInput
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.currentTarget.value)}
                  placeholder="Terminal name"
                  maxLength={MAX_LABEL_LENGTH}
                  error={
                    labelDraft.length > MAX_LABEL_LENGTH
                      ? `Max ${MAX_LABEL_LENGTH} characters`
                      : null
                  }
                  disabled={isSavingLabel}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveLabel(terminal);
                    else if (e.key === "Escape") cancelEditLabel();
                  }}
                  rightSection={
                    <Group gap={2} wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        color="green"
                        onClick={() => saveLabel(terminal)}
                        loading={isSavingLabel}
                        disabled={!labelDraft.trim()}
                        aria-label="Save name"
                      >
                        <IconCheck size="1rem" />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={cancelEditLabel}
                        disabled={isSavingLabel}
                        aria-label="Cancel"
                      >
                        <IconX size="1rem" />
                      </ActionIcon>
                    </Group>
                  }
                  rightSectionWidth={64}
                  w={260}
                />
              ) : (
                <Group gap="xs">
                  <Text size="lg" fw={500}>
                    {displayName}
                  </Text>
                  {details?.status && (
                    <Badge
                      color={getStatusColor(details.status)}
                      variant="light"
                    >
                      {details.status.toUpperCase()}
                    </Badge>
                  )}
                  {canManageTerminals && (
                    <Tooltip label="Rename terminal">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => startEditLabel(terminal, stripeLabel)}
                        aria-label="Rename terminal"
                      >
                        <IconPencil size="0.9rem" />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              )}
              {details?.device_type && (
                <Text size="sm" c="dimmed">
                  Device Type: {deviceTypeToLabel[details.device_type]}
                </Text>
              )}
              {details?.serial_number && (
                <Text size="sm" c="dimmed">
                  Serial Number: {details.serial_number}
                </Text>
              )}
            </Stack>
          </Group>
          {canManageTerminals && (
            <Button
              variant="light"
              color="red"
              size="xs"
              leftSection={<IconTrash size="0.875rem" />}
              onClick={() => setRemoveTarget(terminal)}
            >
              Remove
            </Button>
          )}
        </Group>
      </Paper>
    );
  };

  return (
    <Stack spacing="md" p="md">
      {error && (
        <Text size="md" ta="center" mb="xl" c="red">
          {error}
        </Text>
      )}

      <Group position="apart" justify="space-between">
        <Title order={3}>Card Reader Devices</Title>
        {canManageTerminals && (
          <Group gap="xs">
            {terminals.length > 0 && (
              <Button
                variant="light"
                color="teal"
                leftSection={<IconSettings size="1rem" />}
                onClick={() => setIsConfigModalOpen(true)}
              >
                Configure Tips
              </Button>
            )}
            <Tooltip
              label={`You can attach up to ${MAX_TERMINALS} terminals per location.`}
              disabled={!atCap}
            >
              <Button
                leftSection={<IconPlus size="1rem" />}
                onClick={() => setIsModalOpen(true)}
                color="teal"
                disabled={atCap}
              >
                Add New Device
              </Button>
            </Tooltip>
          </Group>
        )}
      </Group>

      {terminals.length > 0 && (
        <Text size="sm" c="dimmed">
          {terminals.length} of {MAX_TERMINALS} terminals attached. Tip and
          splash settings are shared across all terminals.
        </Text>
      )}

      {terminals.length > 0 ? (
        <Stack gap="md">{terminals.map(renderTerminalCard)}</Stack>
      ) : (
        <Stack gap="md">
          <Text c="dimmed">
            We noticed you do not have a POS device registered to your account.
          </Text>
          <Text c="dimmed">
            If you already have an existing Stripe WisePOS™ E device, please
            register it to begin collecting payments.
          </Text>
          <Text c="dimmed">
            Otherwise, please call us during business hours (Monday-Friday,
            9am-5pm) at (617) 237-6592 or email us at{" "}
            <Text
              component="a"
              href={`mailto:sales@getpagoda.com?subject=POS Order - ${businessInfo?.salon_name}`}
              c="teal"
            >
              sales@getpagoda.com
            </Text>{" "}
            with subject "POS Order - {businessInfo?.salon_name}" and we'll help
            you get set up.
          </Text>
        </Stack>
      )}

      <DeviceRegistrationModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        deviceData={newDeviceData}
        setDeviceData={setNewDeviceData}
        isLoading={isRegistering}
      />

      <DeleteConfirmationModal
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveDevice}
        title="Remove Terminal Device"
        message={`This will disconnect this reader from your store and Stripe account. You can re-register the device later if needed.\n\nAre you sure you want to remove this terminal device?`}
        isLoading={isRemoving}
      />

      <TerminalConfigurationModal
        opened={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        accountId={accountId}
        setTerminalConfiguration={setTerminalConfiguration}
      />
    </Stack>
  );
}

export default Devices;
